import * as styles from "./BreedPage.module.scss";

import { type PageProps, graphql } from "gatsby";
import React, { lazy, useEffect } from "react";

import SSRSuspense from "@dogstrust/dt-web-core-lib-front-end/src/components/SSRSuspense/SSRSuspense";
import SocialSharing from "@dogstrust/dt-web-core-lib-front-end/src/components/common/SocialSharing/SocialSharing";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroBreed from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroBreed/IntroBreed";
import { ParagraphSections } from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import SectionArticleOrEventCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionArticleOrEventCards";
import SectionBreedDogs from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionBreedDogs";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

const FilterPanel = lazy(
	() =>
		import(
			"@dogstrust/dt-web-core-lib-front-end/src/containers/filters/FilterPanel"
		),
);
interface Props extends PageProps {
	data: Queries.BreedQueryQuery;
}

export const Head: React.FC<Props> = ({ data: { nodeBreed } }) => {
	const {
		title,
		path,
		field_hreflang_url,
		field_meta_tags,
		relationships: { field_featured_image },
	} = nodeBreed;
	return (
		<DefaultHead pageUrl={path.alias} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `${title} | Rehoming Rescue Dog | Dogs Trust`,
					description: `Can you give our ${title} Rescue dog their forever home? Find out more, search available dogs and apply to rehome today.`,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_title: `${title} | Rehoming Rescue Dog | Dogs Trust`,
					og_description: `Can you give our ${title} Rescue dog their forever home? Find out more, search available dogs and apply to rehome today.`,
					og_image_url: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: `Can you give our ${title} Rescue dog their forever home? Find out more, search available dogs and apply to rehome today.`,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${title} | Rehoming Rescue Dog | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
				})}
			/>
		</DefaultHead>
	);
};

export const BreedPage: React.FC<Props> = ({ data: { nodeBreed } }) => {
	const {
		path,
		breadcrumbs,
		relationships: {
			field_featured_image,
			field_page_sections,
			field_size,
			field_shedding,
			field_breed,
			field_life_expectancy,
		},
		field_breeds_button_text,
	} = nodeBreed;
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setContentWidth("XXLARGE");
		return () => deregisterPage();
	}, []);

	const additionalarticles =
		nodeBreed.relationships.field_related_content_picker.map((article) => ({
			title: article.title,
			imgSrc:
				article.relationships?.field_featured_image?.field_media_image?.uri,
			imgAlt:
				article.relationships?.field_featured_image?.field_media_image?.alt,
			buttonUrl: article.path?.alias,
			buttonLabel: article.field_short_link_text,
			body: article.field_short_description,
			label: article.relationships?.field_category_type?.name,
		}));

	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<div className={styles.breedpage}>
				<IntroBreed
					title={nodeBreed.title}
					intro={nodeBreed.field_intro_copy}
					imgSrc={field_featured_image?.field_media_image?.uri}
					imgAlt={field_featured_image?.field_media_image?.alt}
					breedSize={field_size.name}
					breedShedding={field_shedding.name}
					breedLifeExpec={field_life_expectancy.name}
				/>

				{field_page_sections?.map((section, i) => {
					const jumpLinksIndex = field_page_sections.findIndex(
						(para) => para.__typename === "paragraph__jump_links",
					);

					return (
						<>
							<ParagraphSections
								key={"id" in section ? section.id : i}
								data={section}
								thisSectionKey={i}
							/>
							{jumpLinksIndex === i && (
								<SectionBreedDogs
									breedNames={field_breed.map((breed) => breed.name)}
									title={`Available ${nodeBreed.title}s`}
									introText={
										"Say hello to these lovely dogs, all looking for their forever home."
									}
									buttonText={
										field_breeds_button_text || `Show all ${nodeBreed.title}s`
									}
								/>
							)}
						</>
					);
				})}
				<div className={styles.sharewrapper}>
					<SocialSharing
						title="If you liked this page, tell the world!"
						shareBodyText="I saw this on the Dogs Trust’s website and thought of you"
						sharePath={process.env.GATSBY_DOMAIN_NAME + path.alias}
					/>
				</div>
			</div>
			{/* Fallback: Render SectionBreedDogs at the end if jumpLinksIndex is not found */}
			{field_page_sections?.every(
				(section) => section.__typename !== "paragraph__jump_links",
			) && (
				<SectionBreedDogs
					breedNames={field_breed.map((breed) => breed.name)}
					title={`Available ${nodeBreed.title}s`}
					introText={
						"Say hello to these lovely dogs, all looking for their forever home."
					}
					buttonText={
						field_breeds_button_text || `Show all ${nodeBreed.title}s`
					}
				/>
			)}

			{additionalarticles.length > 0 && (
				<SectionArticleOrEventCards
					title={"Related dog advice"}
					items={additionalarticles}
					hideFromJumpLinks={true}
				/>
			)}
			<SSRSuspense>
				<FilterPanel shouldInit={false} allCentres={[]} isListingPage={false} />
			</SSRSuspense>
		</Layout>
	);
};
export default BreedPage;

export const query = graphql`	
    query BreedQuery($path: String) {
        nodeBreed(path: { alias: { eq: $path } }) {
            ...BreedData
        }
    }
`;

export const fragment = graphql`
    fragment BreedData on node__breed {
        id
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
		field_hreflang_url
        field_intro_copy
        field_meta_tags {
            ...MetaTags
        }
        field_breeds_button_text
        relationships {
            field_page_sections {
                ...ParagraphSectionsData
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_related_content_picker {
                ...ArticleSignPostData
            }
            field_breed {
                name
            }
            field_size {
                name
            }
            field_shedding {
                name
            }
			field_activity_level {
				name
			}
            field_life_expectancy {
                name
            }
            field_category_type {
                name
            }
        }
    }
`;
